import {
  type FC,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type BoxProps,
  Row,
  Box,
  ChevronDownIcon,
  Text,
  Popover,
  PopoverTrigger,
  Column,
  Portal,
  SearchInput,
  SelectorIcon,
  Tooltip,
  useDisclosure,
  TextWithTooltip,
} from "@hightouch/htui";

import { SkeletonList } from "src/components/skeletons";
import { productNavItemBg } from "src/components/layout/nav/nav-item-styles";
import { InitialTile } from "src/components/layout/nav/initial-tile";
import { useScrollFade } from "src/hooks/use-scroll-fade";
import {
  NAV_POPOVER_MENU_ITEM_STYLES,
  NavPopoverContentAndBody,
} from "./nav-popovers";

const selectorBg = {
  default: "whiteAlpha.60",
  hover: "white",
  open: "white",
};

const SEARCH_THRESHOLD = 5;

export interface NavSelectorOption {
  active: boolean;
  label: string;
  onClick: () => void;
}

export interface NavSelectorProps {
  ariaLabel: string;
  displayLabel: string;
  options: NavSelectorOption[];
  isNavOpen: boolean;
  height?: number;
  topAction?: {
    label: string;
    onClick: () => void;
  };
  isLoading?: boolean;
  isDisabled?: boolean;
  showSearchbar?: boolean;
  searchPlaceholder?: string;
  /** Wider than the field it opens from, so the list reads as its own surface. */
  listWidth?: BoxProps["width"];
  /**
   * `field` is a bordered field; `header` is the AMP nav's header row, flat until hovered.
   * In the header, `leading` sits beside the name and a chevron, or alone in the collapsed rail with the name in its tooltip.
   */
  appearance?: "field" | "header";
  /** Shown before the name in the `header` appearance, such as the workspace's logo. */
  leading?: ReactNode;
}

export const NavSelector: FC<NavSelectorProps> = ({
  ariaLabel,
  displayLabel,
  options,
  isNavOpen,
  height = 10,
  topAction,
  isLoading = false,
  isDisabled = false,
  showSearchbar = false,
  searchPlaceholder = "Search…",
  listWidth = "280px",
  appearance = "field",
  leading,
}) => {
  const isHeaderRow = appearance === "header" && isNavOpen;
  const isHeader = appearance === "header";
  const [search, setSearch] = useState("");
  const { isOpen, onClose, onToggle } = useDisclosure();
  const isLargeSize = height === 10;
  const searchInputRef = useRef<HTMLInputElement>(null);

  const shouldShowSearch = showSearchbar && options.length > SEARCH_THRESHOLD;
  const scrollFade = useScrollFade();

  const filteredOptions = useMemo(
    () =>
      options.filter((option) =>
        option.label.toLowerCase().includes(search.toLowerCase()),
      ),
    [options, search],
  );

  const [isKeyboardClose, setIsKeyboardClose] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = () => setIsKeyboardClose(true);
    const onPointerDown = () => setIsKeyboardClose(false);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [isOpen]);

  const handleClose = () => {
    setSearch("");
    onClose();
  };

  return (
    <Popover
      isLazy
      isOpen={isOpen}
      onClose={handleClose}
      // Focus goes back to the trigger only after a keyboard's close, so a pointer's never leaves it ringed.
      returnFocusOnClose={isKeyboardClose}
      placement="bottom-start"
      initialFocusRef={shouldShowSearch ? searchInputRef : undefined}
    >
      <PopoverTrigger>
        <Row
          aria-label={ariaLabel}
          as="button"
          type="button"
          bg={
            isHeader
              ? isOpen
                ? productNavItemBg.pressed
                : "transparent"
              : isOpen
                ? selectorBg.open
                : selectorBg.default
          }
          borderWidth="hairline"
          borderStyle="solid"
          borderColor={isHeader ? "transparent" : "base.border"}
          borderRadius="lg"
          boxShadow={isHeader ? "none" : "buttonShadowLight"}
          color={isOpen || isHeaderRow ? "text.primary" : "text.tertiary"}
          height={height}
          // The header row hugs its content, so its hover fill ends at the chevron.
          width={isHeaderRow ? "auto" : "100%"}
          maxWidth="100%"
          textAlign="left"
          transitionProperty="background-color, border-color, color"
          transitionDuration="fast"
          transitionTimingFunction="easeOutQuad"
          opacity={isDisabled ? 0.6 : 1}
          cursor={isDisabled ? "default" : "pointer"}
          // A pointer's press draws no ring; only the keyboard's focus does.
          _focus={{ outline: "none", boxShadow: "none" }}
          // Open, its list holds focus and its fill shows it's open, so it never draws a ring.
          _focusVisible={{
            borderColor: "transparent",
            outline: "none",
            boxShadow: isOpen ? "none" : "outline",
          }}
          // Open, the header row keeps its pressed fill under the pointer.
          _hover={
            isDisabled
              ? undefined
              : isHeader
                ? {
                    bg: isOpen
                      ? productNavItemBg.pressed
                      : productNavItemBg.hover,
                  }
                : {
                    bg: isOpen ? selectorBg.open : selectorBg.hover,
                    boxShadow: "buttonShadowLight",
                    color: "text.primary",
                  }
          }
          // After the hover, so a press darkens past it.
          _active={
            isHeader && !isDisabled
              ? { bg: productNavItemBg.pressed }
              : undefined
          }
          onClick={isDisabled ? undefined : onToggle}
        >
          <Tooltip
            isDisabled={isNavOpen}
            message={displayLabel}
            placement="right"
          >
            <Row
              align="center"
              justify={isHeaderRow ? "flex-start" : "center"}
              // Tighter in the header row, where the name shares the width with the inbox and search buttons.
              gap={isHeaderRow ? 1 : 2}
              pl={isHeaderRow ? 1 : isNavOpen ? (isLargeSize ? 3 : 2) : 0}
              pr={isHeaderRow ? 1 : isNavOpen ? (isLargeSize ? 3 : 2) : 0}
              py={isHeaderRow ? 1 : isLargeSize ? 2 : 1.5}
              boxSize="100%"
            >
              {isHeader && leading}
              {isHeaderRow && (
                // Hugs the name, so the chevron follows it rather than the row's end.
                <Box minWidth={0}>
                  {/* A step under the nav rows' type, so more of a long name shows beside the header's buttons. */}
                  <TextWithTooltip
                    size="sm"
                    fontWeight="semibold"
                    color="inherit"
                    placement="right"
                  >
                    {displayLabel}
                  </TextWithTooltip>
                </Box>
              )}
              {isNavOpen && !isHeaderRow && (
                <Box
                  as={Text}
                  color="inherit"
                  flex={1}
                  fontSize={isLargeSize ? "md" : "sm"}
                  fontWeight="semibold"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  overflow="hidden"
                >
                  {displayLabel}
                </Box>
              )}

              {/* The first letter says which one is open, since the collapsed rail has no room for the name. */}
              {!isNavOpen && <InitialTile name={displayLabel} />}

              {!isDisabled && isHeaderRow && (
                <Box
                  flexShrink={0}
                  as={ChevronDownIcon}
                  color="text.tertiary"
                  boxSize={4}
                />
              )}

              {!isDisabled && isNavOpen && !isHeaderRow && (
                <Box
                  flexShrink={0}
                  as={SelectorIcon}
                  color="text.tertiary"
                  boxSize={isLargeSize ? 4 : 3.5}
                />
              )}
            </Row>
          </Tooltip>
        </Row>
      </PopoverTrigger>
      <Portal>
        <NavPopoverContentAndBody width={listWidth}>
          {shouldShowSearch && (
            <Box
              px={3}
              pt={2}
              pb={2}
              borderBottomWidth="hairline"
              borderBottomStyle="solid"
              borderColor="base.divider"
            >
              <SearchInput
                ref={searchInputRef}
                placeholder={searchPlaceholder}
                value={search}
                width="100%"
                size="md"
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
          )}
          {isLoading ? (
            <SkeletonList gap={1.5} p={1.5}>
              <Box borderRadius="xl" h="30px" w="90%" />
              <Box borderRadius="xl" h="30px" w="70%" />
              <Box borderRadius="xl" h="30px" w="80%" />
            </SkeletonList>
          ) : filteredOptions.length === 0 && search ? (
            <Box px={3} py={4} color="text.secondary" fontSize="sm">
              No results found
            </Box>
          ) : (
            <Column
              ref={scrollFade.ref}
              mt={2}
              overflow="auto"
              maxHeight="480px"
              sx={scrollFade.sx}
            >
              {filteredOptions.map(({ active, label, onClick }) => (
                <Box
                  key={label}
                  as="button"
                  display="flex"
                  alignItems="center"
                  height={9}
                  textAlign="left"
                  bg={active ? "primary.background" : undefined}
                  role="group"
                  pointerEvents={active ? "none" : undefined}
                  _hover={{ bg: "base.background" }}
                  onClick={() => {
                    onClick();
                    handleClose();
                  }}
                  {...NAV_POPOVER_MENU_ITEM_STYLES}
                >
                  <Box overflow="hidden" flex={1} minWidth={0}>
                    <TextWithTooltip
                      color={active ? "primary.base" : "text.secondary"}
                    >
                      {label}
                    </TextWithTooltip>
                  </Box>
                </Box>
              ))}
            </Column>
          )}
          {topAction && (
            <Column
              borderTopWidth="hairline"
              borderTopStyle="solid"
              borderColor="base.divider"
            >
              <Box
                mt={2}
                as="button"
                textAlign="left"
                fontSize="md"
                lineHeight="20px"
                height={9}
                color="text.secondary"
                _hover={{ bg: "base.background", color: "text.primary" }}
                onClick={() => {
                  topAction.onClick();
                  handleClose();
                }}
                {...NAV_POPOVER_MENU_ITEM_STYLES}
              >
                {topAction.label}
              </Box>
            </Column>
          )}
        </NavPopoverContentAndBody>
      </Portal>
    </Popover>
  );
};
